import { dispatcher, DispatcherEvents } from "./dispatcher";
import { hashToArray, echo, extend } from "./vanilla";
import { ITimeZoneInfo } from "./date";
import User from "../model/User";
import { DriveStore } from "../store/reducers";
import { actions } from "../store/users/actions";
import { ActiveUserActions } from "../store/active/actions";
import { BaseCache, makeUserCache } from "./storage/cache";
import { DriverSeatUserActions } from "../store/driverSeat/actions";

export function sanitizeUserName(name: string) {
    return name.toLowerCase();
}

export class UserService {
    private readonly userCache: BaseCache<IUser>;

    constructor(private readonly store: DriveStore) {
        this.userCache = makeUserCache(store);
    }

    removeDuplicateNames(names: string[]) {
        let seen = {};

        return names.filter(name => {
            if (seen[name]) {
                return false;
            } else {
                seen[name] = true;
                return true;
            }
        });
    }

    // General wrapper methods for users in storage
    getUsers(activeOnly = false): DeepReadonly<IUser>[] {
        let users = this.getUsersObject();

        if (activeOnly) {
            let activeUserNames = this.getActiveUserNames();
            let activeUsersArray: DeepReadonly<IUser>[] = [];

            activeUserNames.forEach(u => {
                if (users.hasOwnProperty(u)) {
                    activeUsersArray.push(users[u]);
                }
            });

            return activeUsersArray;
        }

        return hashToArray(users);
    }

    getSortedUsers(activeOnly = false) {
        return (this.getUsers(activeOnly) || [])
            .map(user => new User(user))
            .sort((user1, user2) => {
                let user1Name = user1.getFullName();
                let user2Name = user2.getFullName();
                return user1Name > user2Name ? 1 : (user1Name < user2Name ? -1 : 0);
            });
    }

    addUser(user: IUser) {
        this.setUser(user);
        this.addActiveUser(user.name);

        // Check to see if there are any drivers
        // If not, make this user the primary driver
        if (!this.getDriversSeatUser()) {
            this.setDriversSeatUser(user.name);
        }

        this.getUsers(true).forEach(activeUser => {
            if (!this.userCache.get(user, activeUser.id).length) {
                this.userCache.add([user], activeUser.id);
            }
        });
    }

    setUser(user: DeepReadonly<IUser>) {
        let users = {...this.getUsersObject()};
        let sanitizedUserName = sanitizeUserName(user.name);

        let modifiedUser = extend(true, {}, user) as IUser;
        modifiedUser.name = sanitizedUserName;

        users[sanitizedUserName] = modifiedUser;
        this.store.dispatch(actions.setUsers(users as IIdDictionary<IUser>));
    }

    removeUser(userName: string) {
        let sanitizedUserName = sanitizeUserName(userName);
        let users = {...this.getUsersObject()};

        // First remove any active users by this name
        this.removeActiveUser(sanitizedUserName);

        // Then remove from the users object
        if (users.hasOwnProperty(sanitizedUserName)) {
            delete users[sanitizedUserName];
            this.store.dispatch(actions.setUsers(users as IIdDictionary<IUser>));
        }
    }

    /**
     * Returns a list of users' names that are present in `@store_users` but not `@store_activeUsers`
     */
    getInactiveUsers() {
        let activeUserNames = this.store.getState().activeUsers as string[];

        let usersInStore = this.getUsersObject();
        let activeUsers: IIdDictionary<DeepReadonly<IUser>> = {};
        let sanitizedUserNames = activeUserNames.map(sanitizeUserName);

        if (usersInStore) {
            let inactiveUserNames: string[] = [];
            for (let user in usersInStore) {
                if (sanitizedUserNames.indexOf(user) === -1) {
                    inactiveUserNames.push(usersInStore[user].name);
                } else {
                    activeUsers[user] = { ...usersInStore[user] };
                }
            }

            return inactiveUserNames;
        }

        return [];
    }

    getUsersObject() {
        return extend(true, {}, this.store.getState().users);
    }

    // Methods for interacting with active users (currently signed in), where the order dictates who is currently
    // viewing the app
    getActiveUserNames(): string[] {
        const activeUsers = this.store.getState().activeUsers as DeepReadonlyArray<string>;
        return activeUsers ? [...activeUsers] : [];
    }

    setActiveUserNames(userNames: string[]) {
        this.store.dispatch(ActiveUserActions.set(this.removeDuplicateNames(userNames.map(sanitizeUserName))));
    }

    setCurrentActiveUser(userName: string) {
        let currentUsers = this.getActiveUserNames();
        let index = currentUsers.indexOf(userName);

        if (index !== -1) {
            currentUsers.splice(index, 1);
        }

        currentUsers.unshift(userName);
        this.setActiveUserNames(currentUsers);
        dispatcher.trigger(DispatcherEvents.UserSwitch, userName);
    }

    addActiveUser(userName: string) {
        let sanitizedUserName = sanitizeUserName(userName);
        let currentUsers = this.getActiveUserNames();

        if (currentUsers.indexOf(sanitizedUserName) === -1) {
            currentUsers.push(sanitizedUserName);
            this.setActiveUserNames(currentUsers);
        }
    }

    removeActiveUser(userName: string) {
        let sanitizedUserName = sanitizeUserName(userName);
        let activeUsers = this.removeDuplicateNames(this.getActiveUserNames());
        let currentDriver = this.getDriversSeatUser();
        let index = activeUsers.indexOf(sanitizedUserName);

        if (index !== -1) {
            activeUsers.splice(index, 1);
            this.setActiveUserNames(activeUsers);
        }

        // Switch the driver's seat to the next user in line (if the user logging out is in the driver's seat)
        if (currentDriver && sanitizeUserName(currentDriver.name) === sanitizedUserName && activeUsers && activeUsers.length) {
            this.setDriversSeatUser(activeUsers[0], false, true);
        }
    }

    // Methods for interacting with the driver currently in the driver's seat
    getDriversSeatUser() {
        let activeDriverName = this.store.getState().driverSeatUser;
        let activeUserNames = this.getActiveUserNames();

        if (!activeDriverName) {
            return null;
        }

        if (activeUserNames.indexOf(activeDriverName) !== -1) {
            let user = this.getUserByName(activeDriverName);
            return user || { name: activeDriverName, id: "" };
        }

        return null;
    }

    /**
     * Sets the driver in the driver seat.
     * @param userName The username of the new driver.
     * @param isVehicleSelect Is the driver being set because a vehicle is being selected?
     * @param removeActiveUser Is the driver being set because the original driver was removed from the app?
     * @param isAlreadyAssigned Was the driver assigned to the vehicle externally? (ie. via admin during trip assignment or log reassignment)
     */
    setDriversSeatUser(userName: string, isVehicleSelect = false, isRemoveActiveDriver = false, isAlreadyAssigned = false) {
        let previousDriver = this.getDriversSeatUser();
        let sanitizedUserName = sanitizeUserName(userName);

        if (!previousDriver || sanitizedUserName !== sanitizeUserName(previousDriver.name)) {
            this.store.dispatch(DriverSeatUserActions.set(sanitizedUserName));
            dispatcher.trigger(DispatcherEvents.DriverSwitch, sanitizedUserName, isVehicleSelect, isRemoveActiveDriver, previousDriver, isAlreadyAssigned);
        }
    }

    getCoDrivers(userName = "", isFull = false) {
        let users = this.getUsersObject();
        let sanitizedUserName = sanitizeUserName(userName);
        let otherActiveUserNames = this.getActiveUserNames().filter(name => !(name === sanitizedUserName));
        let activeUsersArray: IIdEntity[] = [];

        otherActiveUserNames.forEach(activeUserName => {
            let thisUser = users[activeUserName];
            thisUser && activeUsersArray.push(isFull ? thisUser : { id: thisUser.id });
        });

        return activeUsersArray;
    }

    getUserByName(userName: string): DeepReadonly<IUser> | null {
        let sanitizedUserName = sanitizeUserName(userName);
        let users = this.getUsersObject();

        return users.hasOwnProperty(sanitizedUserName) ? users[sanitizedUserName] : null;
    }

    // UI methods
    getCurrentUser(): DeepReadonly<IUser> | null {
        let users = this.getUsers(true);
        return users[0] || null;
    }

    isDriverUsingUI(userName: string): boolean {
        let currUser = this.getCurrentUser();

        if (currUser && currUser.name && userName) {
            return sanitizeUserName(currUser.name) === sanitizeUserName(userName);
        }

        return false;
    }

    hasLoggedInUser(): boolean {
        let users = this.getUsers(true);
        return users && users.length > 0;
    }

    getUserById(userId: string, activeOnly = true) {
        let users = this.getUsers(activeOnly) || [];

        for (let user of users) {
            if (user && user.id === userId) {
                return user;
            }
        }

        return null;
    }

    getUsersTimezoneInfo() {
        let users = this.getUsers(true) || [];
        return users.map(user => extend(true, {}, this.store.getState().timeZoneInfo[user.id] || {})).filter(echo) as ITimeZoneInfo[];
    }
}
